import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useNotifications, type UseNotificationsReturn } from '../hooks/useNotifications'

const NotificationsContext = createContext<UseNotificationsReturn | null>(null)

/**
 * Whether a Notification Center panel is open, wherever it was opened from
 * (bell or desktop widget area). Each open panel reports itself, so this is
 * the one place that knows — toasts hold back while it is true (#1093).
 */
export interface NotificationPanelState {
  isPanelOpen: boolean
  panelOpened: () => void
  panelClosed: () => void
}

const NotificationPanelContext = createContext<NotificationPanelState | null>(null)

export const NotificationPanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [openPanels, setOpenPanels] = useState(0)
  const panelOpened = useCallback(() => setOpenPanels((count) => count + 1), [])
  const panelClosed = useCallback(() => setOpenPanels((count) => Math.max(0, count - 1)), [])
  const value = useMemo(
    () => ({ isPanelOpen: openPanels > 0, panelOpened, panelClosed }),
    [openPanels, panelOpened, panelClosed],
  )
  return <NotificationPanelContext.Provider value={value}>{children}</NotificationPanelContext.Provider>
}

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hook = useNotifications()
  return (
    <NotificationsContext.Provider value={hook}>
      <NotificationPanelProvider>{children}</NotificationPanelProvider>
    </NotificationsContext.Provider>
  )
}

export const useNotificationsContext = (): UseNotificationsReturn => {
  const ctx = useContext(NotificationsContext)
  if (!ctx) throw new Error('useNotificationsContext must be used within NotificationsProvider')
  return ctx
}

/**
 * The same context, but null instead of a throw when there is no provider.
 *
 * For consumers that only *react* to notifications rather than display them —
 * they should degrade to their own polling, not take the surrounding UI down
 * with them (and the component tests render them without a provider).
 */
export const useNotificationsContextOptional = (): UseNotificationsReturn | null =>
  useContext(NotificationsContext)

/** Panel-open state; null without a provider (the panel then just does not report). */
export const useNotificationPanel = (): NotificationPanelState | null => useContext(NotificationPanelContext)
